"use client";

import { Calendar } from "lucide-react";
import { useTranslations } from "next-intl";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import DocumentoItem from "./DocumentoItem";

interface DocumentosPorAnioProps {
  documentosPorAnio: {
    anio: number;
    documentos: {
      id: number;
      titulo: string;
      archivo: string;
      formato: string;
      anio: number;
      seguimientos?: {
        id: number;
        tipo: string;
        titulo: string;
        archivo: string;
        formato: string;
      }[];
    }[];
  }[];
  /**
   * "gray" → card gris (pop contra contenedor blanco, p.ej. Grupo).
   * "white" → card blanca (pop contra contenedor gris, p.ej. directo en Subarticle).
   */
  cardVariant?: "gray" | "white";
  /**
   * Años abiertos (`anio-2026`). Si se pasa, el acordeón queda controlado por
   * el llamador — Información Financiera lo usa para abrir los años que
   * coinciden con la búsqueda. Sin él sigue siendo autónomo, como en el
   * listado de Transparencia.
   */
  openYears?: string[];
  onOpenYearsChange?: (value: string[]) => void;
}

export default function DocumentosPorAnio({
  documentosPorAnio,
  cardVariant = "gray",
  openYears,
  onOpenYearsChange,
}: DocumentosPorAnioProps) {
  const t = useTranslations("transparenciaPage");
  if (!documentosPorAnio || documentosPorAnio.length === 0) return null;

  const cardBg = cardVariant === "gray" ? "bg-gray-50" : "bg-white";

  return (
    <Accordion
      type="multiple"
      className="space-y-2"
      {...(openYears ? { value: openYears, onValueChange: onOpenYearsChange } : {})}
    >
      {documentosPorAnio.map((docAnio) => (
        <AccordionItem
          key={docAnio.anio}
          value={`anio-${docAnio.anio}`}
          className={`${cardBg} border border-gray-200 rounded-xl overflow-hidden`}
        >
          <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-gray-100/60 transition-colors">
            <div className="flex items-center gap-2.5">
              <Calendar className="w-4 h-4 text-[#102542]" />
              <span className="font-semibold text-sm text-gray-900 tabular-nums">
                {docAnio.anio}
              </span>
              <span className="text-[10px] uppercase tracking-wider font-semibold text-[#102542] border border-[#102542]/30 bg-[#102542]/5 px-2 py-0.5 rounded-full">
                {t("documentosCount", { count: docAnio.documentos.length })}
              </span>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-4 pb-4 overflow-hidden border-t border-gray-100 bg-white">
            <div className="grid gap-2 pt-3 overflow-hidden">
              {docAnio.documentos.map((documento) => (
                <DocumentoItem key={documento.id} documento={documento} />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
